import { defineMessages, IntlShape } from 'react-intl';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCalendarDays } from '@fortawesome/pro-light-svg-icons/faCalendarDays';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { faRocketLaunch } from '@fortawesome/pro-light-svg-icons/faRocketLaunch';
import type { ReleaseNextStep, ReleaseSetupDetail } from 'fym-shared/src/music24/releases';
import { platformMeta } from '../../platformMeta';
import { SocialPlatform } from '../../types';
import { releaseSetupShortNames } from '../setup/releaseSetupMessages';

/** The look of the next-step mark (design a1 `.nx .k`). */
export type NextStepTone = 'warn' | 'hot' | 'ok' | 'plain';

export interface NextStepText {
  title: string;
  detail: string;
  tone: NextStepTone;
  icon: IconDefinition;
}

const messages = defineMessages({
  addMaster: { id: 'app.sml.releases.next.addMaster', defaultMessage: 'Add a master' },
  fixMaster: { id: 'app.sml.releases.next.fixMaster', defaultMessage: 'Upload the master again' },
  setDate: { id: 'app.sml.releases.next.setDate', defaultMessage: 'Set a release date' },
  setDateDetail: {
    id: 'app.sml.releases.next.setDateDetail',
    defaultMessage: 'Then plan the rollout. Fix it in Catalog.',
  },
  addCover: { id: 'app.sml.releases.next.addCover', defaultMessage: 'Add the cover' },
  addCoverAndDate: { id: 'app.sml.releases.next.addCoverAndDate', defaultMessage: 'Add the cover and the date' },
  addLyrics: { id: 'app.sml.releases.next.addLyrics', defaultMessage: 'Add the lyrics' },
  setTiming: { id: 'app.sml.releases.next.setTiming', defaultMessage: 'Set the lyric timing' },
  fixTiming: { id: 'app.sml.releases.next.fixTiming', defaultMessage: 'Fix the lyric timing' },
  addSmartLink: { id: 'app.sml.releases.next.addSmartLink', defaultMessage: 'Add a SmartLink' },
  publishSmartLink: { id: 'app.sml.releases.next.publishSmartLink', defaultMessage: 'Publish the SmartLink' },
  turnOnPresave: { id: 'app.sml.releases.next.turnOnPresave', defaultMessage: 'Turn on pre-save' },
  waitMaster: { id: 'app.sml.releases.next.waitMaster', defaultMessage: 'Reading the master' },
  missingIn: { id: 'app.sml.releases.next.missingIn', defaultMessage: 'Missing in Catalog: {items}' },
  failed: {
    id: 'app.sml.releases.next.failed',
    defaultMessage: '{count, plural, one {# asset failed} other {# assets failed}}',
  },
  failedDetail: {
    id: 'app.sml.releases.next.failedDetail',
    defaultMessage: 'Try again on the Assets tab. The credits came back.',
  },
  outToday: { id: 'app.sml.releases.next.outToday', defaultMessage: 'Out today' },
  outTodayDetail: { id: 'app.sml.releases.next.outTodayDetail', defaultMessage: 'Check the out-now posts.' },
  outSoon: {
    id: 'app.sml.releases.next.outSoon',
    defaultMessage: '{days, plural, one {Out tomorrow} other {Out in # days}}',
  },
  outSoonDetail: { id: 'app.sml.releases.next.outSoonDetail', defaultMessage: 'Check the countdown posts.' },
  fillGaps: {
    id: 'app.sml.releases.next.fillGaps',
    defaultMessage: '{count, plural, one {Fill # gap in the rollout} other {Fill # gaps in the rollout}}',
  },
  fillGapsDetail: {
    id: 'app.sml.releases.next.fillGapsDetail',
    defaultMessage: '{days} {count, plural, one {has no post} other {have no post}}',
  },
  postsThisWeek: {
    id: 'app.sml.releases.next.postsThisWeek',
    defaultMessage: '{count, plural, one {# post this week} other {# posts this week}}',
  },
  nextOn: { id: 'app.sml.releases.next.nextOn', defaultMessage: 'Next: {when} on {platform}' },
  nextAt: { id: 'app.sml.releases.next.nextAt', defaultMessage: 'Next: {when}' },
  postsPlanned: {
    id: 'app.sml.releases.next.postsPlanned',
    defaultMessage: '{count, plural, one {# post planned} other {# posts planned}}',
  },
  planRollout: { id: 'app.sml.releases.next.planRollout', defaultMessage: 'Plan the rollout' },
  planRolloutDetail: { id: 'app.sml.releases.next.planRolloutDetail', defaultMessage: 'Nothing planned yet.' },
  planPush: { id: 'app.sml.releases.next.planPush', defaultMessage: 'Plan a push' },
  planPushDetail: {
    id: 'app.sml.releases.next.planPushDetail',
    defaultMessage: 'Nothing planned. One post a week keeps the song in the feed.',
  },
  makeSomething: { id: 'app.sml.releases.next.makeSomething', defaultMessage: 'Make something for it' },
  makeSomethingDetail: {
    id: 'app.sml.releases.next.makeSomethingDetail',
    defaultMessage: 'Nothing made yet. Open the Studio.',
  },
});

const SETUP_TITLES: Record<ReleaseSetupDetail, keyof typeof messages> = {
  noMaster: 'addMaster',
  masterProcessing: 'waitMaster',
  masterFailed: 'fixMaster',
  noCover: 'addCover',
  noDate: 'setDate',
  noCoverNoDate: 'addCoverAndDate',
  noLyrics: 'addLyrics',
  noTiming: 'setTiming',
  timingSyncing: 'setTiming',
  timingFailed: 'fixTiming',
  noSmartLink: 'addSmartLink',
  smartLinkDraft: 'publishSmartLink',
  noPresave: 'turnOnPresave',
};

/** "Thu 9 Oct, 18:00" in the viewer's time zone. */
export function formatPostTime(intl: IntlShape, iso: string): string {
  return intl.formatDate(new Date(iso), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "Mon 13 Oct" of a `YYYY-MM-DD` day (a day, so read in UTC). */
export function formatPlanDay(intl: IntlShape, day: string): string {
  return intl.formatDate(new Date(`${day}T00:00:00Z`), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function platformName(platform: string | null): string | null {
  return platform !== null && platform in platformMeta ? platformMeta[platform as SocialPlatform].name : null;
}

/**
 * The one line a Releases row leads with (design a1): a title, a detail line, a tone and an icon.
 * The server picks the step (`releaseNextStep` in fym-shared `releases.ts`); this only words it.
 */
export function releaseNextStepText(intl: IntlShape, step: ReleaseNextStep): NextStepText {
  switch (step.kind) {
    case 'setup': {
      const items = intl.formatList(
        step.missing.map((key) => intl.formatMessage(releaseSetupShortNames[key])),
        {
          type: 'conjunction',
        }
      );
      const isDate = step.detail === 'noDate';
      return {
        title: intl.formatMessage(messages[SETUP_TITLES[step.detail]]),
        detail: isDate ? intl.formatMessage(messages.setDateDetail) : intl.formatMessage(messages.missingIn, { items }),
        tone: 'warn',
        icon: isDate ? faCalendarDays : faTriangleExclamation,
      };
    }
    case 'failed':
      return {
        title: intl.formatMessage(messages.failed, { count: step.count }),
        detail: intl.formatMessage(messages.failedDetail),
        tone: 'warn',
        icon: faCircleExclamation,
      };
    case 'outToday':
      return {
        title: intl.formatMessage(messages.outToday),
        detail: intl.formatMessage(messages.outTodayDetail),
        tone: 'hot',
        icon: faRocketLaunch,
      };
    case 'outSoon':
      return {
        title: intl.formatMessage(messages.outSoon, { days: step.days }),
        detail: intl.formatMessage(messages.outSoonDetail),
        tone: 'hot',
        icon: faCalendarDays,
      };
    case 'fillGaps': {
      const days = intl.formatList(
        step.days.map((day) => formatPlanDay(intl, day)),
        { type: 'conjunction' }
      );
      return {
        title: intl.formatMessage(messages.fillGaps, { count: step.count }),
        detail: intl.formatMessage(messages.fillGapsDetail, { days, count: step.count }),
        tone: 'hot',
        icon: faCalendarDays,
      };
    }
    case 'postsThisWeek': {
      const when = formatPostTime(intl, step.nextAt);
      const platform = platformName(step.nextPlatform);
      return {
        title: intl.formatMessage(messages.postsThisWeek, { count: step.count }),
        detail:
          platform === null
            ? intl.formatMessage(messages.nextAt, { when })
            : intl.formatMessage(messages.nextOn, { when, platform }),
        tone: 'ok',
        icon: faPaperPlane,
      };
    }
    case 'postsPlanned':
      return {
        title: intl.formatMessage(messages.postsPlanned, { count: step.count }),
        detail:
          step.nextAt === null ? '' : intl.formatMessage(messages.nextAt, { when: formatPostTime(intl, step.nextAt) }),
        tone: 'ok',
        icon: faPaperPlane,
      };
    case 'planRollout':
      return {
        title: intl.formatMessage(messages.planRollout),
        detail: intl.formatMessage(messages.planRolloutDetail),
        tone: 'plain',
        icon: faCalendarDays,
      };
    case 'planPush':
      return {
        title: intl.formatMessage(messages.planPush),
        detail: intl.formatMessage(messages.planPushDetail),
        tone: 'plain',
        icon: faArrowsRotate,
      };
    case 'makeSomething':
      return {
        title: intl.formatMessage(messages.makeSomething),
        detail: intl.formatMessage(messages.makeSomethingDetail),
        tone: 'plain',
        icon: faWandMagicSparkles,
      };
    default: {
      const unknown: never = step;
      return unknown;
    }
  }
}
